import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@capsico/ui';
import { MAX_LINE_QUANTITY, type OfferView } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { useCheckout } from '../hooks/useCheckout';
import { cashbackEstimate, useCashback } from '../hooks/useCashback';
import { CashbackBanner } from '../components/Growth';
import { addressLine, addressTitle } from '../hooks/useAddresses';
import { money } from '../utils/money';
import { dayMonth, etaText, slotText } from '../utils/format';
import {
  ActionRow,
  AddressSheet,
  BillCard,
  CartLineRow,
  OffersSheet,
  PayBar,
  TipCard,
  WhySheet,
} from '../components/Checkout';
import {
  Button,
  Card,
  DraftBar,
  EmptyState,
  Field,
  ListSkeleton,
  Notice,
  PageHeader,
  Pill,
  Toggle,
} from '../components/ui';
import { MethodList, PayNote, PaymentSection, WalletToggleRow } from '../components/Payment';

/**
 * Cart + checkout in one page (C13–C18): the server-priced cart, kitchen note and cutlery,
 * offers (best one applied automatically), free-delivery and minimum-order notices, tip,
 * delivery address / someone else / schedule, the bill with "why this fee" sheets, payment
 * (COD tiers, dev test payment) and a pinned "Place order" bar. All state lives in useCheckout.
 */
export function CheckoutPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const c = useCheckout({
    pricesChanged: t('checkout.pricesChanged'),
    placed: t('checkout.placed'),
    deleted: t('address.deleted'),
  });
  const { cashback } = useCashback(c.signedIn);
  const [sheet, setSheet] = useState<'offers' | 'address' | null>(null);
  const names = { home: t('address.home'), work: t('address.work'), other: t('address.other') };
  const funding = (value: OfferView['fundedBy']) => t(`checkout.funding.${value}`);

  // After placing, go to the order (replace so Back does not return to an empty cart).
  useEffect(() => {
    if (!c.placedOrder) return;
    // Online orders open the gateway from the order page (it also handles retry and cancel).
    const pay = c.placedOrder.stage === 'awaiting_payment' ? '&pay=1' : '';
    navigate(`/orders/${c.placedOrder.id}?placed=1${pay}`, { replace: true });
  }, [c.placedOrder, navigate]);

  const header = (
    <PageHeader
      title={c.cart?.outletName ?? t('checkout.title')}
      onBack={() => navigate(-1)}
      backLabel={t('common.back')}
    />
  );

  if (!c.cart) {
    return (
      <div className="min-h-dvh bg-page">
        {header}
        <EmptyState
          icon="bag"
          title={t('cart.empty')}
          action={<Button onClick={() => navigate('/')}>{t('cart.browse')}</Button>}
        >
          {t('cart.emptyHint')}
        </EmptyState>
      </div>
    );
  }

  const cart = c.cart;
  const country = cart.country;
  const q = c.quote;
  const quantityOf = (index: number) => cart.lines[index]?.quantity ?? 0;
  const keyOf = (index: number) => cart.lines[index]?.key ?? '';
  const appliedOffer = q?.offers.find((offer) => offer.applied) ?? null;
  const wallet = q?.payment.wallet ?? null;
  const walletUsed = wallet && c.useWallet ? wallet.applyMinor : 0;
  const payable = q ? money(country, q.payment.payableMinor) : '';
  const cashbackHint = q ? cashbackEstimate(cashback, country, q.bill.totalMinor) : null;
  const payLabel =
    c.payKind === 'wallet'
      ? t('pay.payWallet')
      : c.payKind === 'online'
        ? t('pay.payAmount', { amount: payable })
        : t('checkout.place');

  return (
    <div className="min-h-dvh bg-page pb-36">
      {header}
      <div className="flex flex-col gap-5 px-4 pt-4">
        {c.draftRestored && (
          <DraftBar
            text={t('common.draftRestored')}
            keepLabel={t('common.keep')}
            discardLabel={t('common.discard')}
            onKeep={c.keepDraft}
            onDiscard={c.discardDraft}
          />
        )}

        {c.quoteLoading ? (
          <Card>
            <ListSkeleton rows={4} />
          </Card>
        ) : c.quoteFailed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('checkout.unavailable')}
            action={
              <Button size="sm" tone="outline" onClick={c.retryQuote}>
                {t('common.retry')}
              </Button>
            }
          >
            {c.quoteError}
          </Notice>
        ) : (
          q && (
            <>
              {(!q.outlet.open || !q.outlet.deliverable) && q.outlet.reason && (
                <Notice tone="warning" title={q.outlet.reason} />
              )}

              {/* Items + kitchen note + cutlery */}
              <Card>
                <div className="divide-y divide-hair">
                  {q.lines.map((line) => (
                    <CartLineRow
                      key={`${line.index}-${line.itemId}`}
                      line={line}
                      country={country}
                      quantity={quantityOf(line.index)}
                      canIncrease={quantityOf(line.index) < MAX_LINE_QUANTITY}
                      onIncrease={() =>
                        c.setQuantity(keyOf(line.index), quantityOf(line.index) + 1)
                      }
                      onDecrease={() =>
                        c.setQuantity(keyOf(line.index), quantityOf(line.index) - 1)
                      }
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/r/${cart.outletId}`)}
                  className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-brand-ink"
                >
                  <Icon name="plus" size={14} />
                  {t('checkout.addMore')}
                </button>
                <div className="mt-4 space-y-4 pt-4 shadow-[inset_0_1px_0_var(--color-hair)]">
                  <Field
                    label={t('checkout.kitchenNote')}
                    placeholder={t('checkout.kitchenNoteHint')}
                    maxLength={300}
                    value={c.form.kitchenNote}
                    onChange={(event) => c.setField('kitchenNote', event.target.value)}
                  />
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink">{t('checkout.cutlery')}</p>
                      <p className="text-caption text-ink-3">{t('checkout.cutleryHint')}</p>
                    </div>
                    <Toggle
                      on={c.form.cutlery}
                      onChange={(next) => c.setField('cutlery', next)}
                      label={t('checkout.cutlery')}
                    />
                  </div>
                </div>
              </Card>

              {q.minimumOrder.shortByMinor > 0 && (
                <Notice
                  tone="warning"
                  title={t('checkout.minimum', {
                    amount: money(country, q.minimumOrder.shortByMinor),
                  })}
                />
              )}

              {/* Offers */}
              <Card>
                {appliedOffer ? (
                  <ActionRow
                    icon="tag"
                    title={t('checkout.applied', { code: appliedOffer.code })}
                    subtitle={
                      <span className="font-semibold text-positive">
                        {t('checkout.youSave', {
                          amount: money(country, appliedOffer.discountMinor),
                        })}
                      </span>
                    }
                    trailing={
                      <button
                        type="button"
                        onClick={c.removeCoupon}
                        className="text-xs font-semibold text-negative"
                      >
                        {t('checkout.remove')}
                      </button>
                    }
                  />
                ) : (
                  <ActionRow
                    icon="tag"
                    title={t('checkout.offers')}
                    subtitle={t('checkout.viewOffers')}
                    onClick={() => setSheet('offers')}
                  />
                )}
                {q.couponError && c.coupon && (
                  <p className="mt-2 text-caption text-negative">
                    {c.coupon}: {q.couponError}
                  </p>
                )}
                {appliedOffer && (
                  <button
                    type="button"
                    onClick={() => setSheet('offers')}
                    className="mt-3 text-xs font-semibold text-brand-ink"
                  >
                    {t('checkout.viewOffers')}
                  </button>
                )}
              </Card>

              {q.freeDelivery.applied && (
                <Notice tone="positive" title={t('checkout.freeDelivery')}>
                  {q.freeDelivery.ordersLeft > 0
                    ? t('checkout.freeLeft', { count: q.freeDelivery.ordersLeft })
                    : null}
                </Notice>
              )}

              {/* Delivery: address, someone else, when */}
              <Card>
                {c.signedIn && c.address ? (
                  <ActionRow
                    icon={
                      c.address.label === 'home'
                        ? 'home'
                        : c.address.label === 'work'
                          ? 'store'
                          : 'pin'
                    }
                    title={`${t('checkout.deliverTo')} ${addressTitle(c.address, names)}`}
                    subtitle={addressLine(c.address)}
                    trailing={
                      <button
                        type="button"
                        onClick={() => setSheet('address')}
                        className="text-xs font-semibold text-brand-ink"
                      >
                        {t('checkout.change')}
                      </button>
                    }
                  />
                ) : c.signedIn && !c.addressesLoading ? (
                  <ActionRow
                    icon="pin"
                    title={t('checkout.needAddress')}
                    onClick={() => navigate('/address/new')}
                  />
                ) : (
                  <ActionRow
                    icon="pin"
                    title={c.point?.label ?? t('location.choose')}
                    subtitle={c.point?.line ?? undefined}
                  />
                )}
                {q.eta && (
                  <p className="mt-3 inline-flex items-center gap-1 text-xs text-ink-2 tabular">
                    <Icon name="clock" size={14} />
                    {etaText(q.eta)}
                  </p>
                )}

                {c.signedIn && (
                  <div className="mt-4 space-y-3 pt-4 shadow-[inset_0_1px_0_var(--color-hair)]">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-ink">
                          {t('checkout.leaveAtDoor')}
                        </p>
                        <p className="text-caption text-ink-3">{t('checkout.leaveAtDoorHint')}</p>
                      </div>
                      <Toggle
                        on={c.form.leaveAtDoor}
                        onChange={(next) => c.setField('leaveAtDoor', next)}
                        label={t('checkout.leaveAtDoor')}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="min-w-0 flex-1 text-sm font-semibold text-ink">
                        {t('checkout.someoneElse')}
                      </p>
                      <Toggle
                        on={c.form.forSomeoneElse}
                        onChange={(next) => c.setField('forSomeoneElse', next)}
                        label={t('checkout.someoneElse')}
                      />
                    </div>
                    {c.form.forSomeoneElse && (
                      <>
                        <Field
                          label={t('checkout.receiverName')}
                          maxLength={60}
                          value={c.form.receiverName}
                          onChange={(event) => c.setField('receiverName', event.target.value)}
                        />
                        <Field
                          label={t('checkout.receiverPhone')}
                          inputMode="tel"
                          maxLength={16}
                          value={c.form.receiverPhone}
                          error={c.receiverPhoneError ? t('checkout.phoneError') : null}
                          onChange={(event) => c.setField('receiverPhone', event.target.value)}
                        />
                      </>
                    )}
                  </div>
                )}

                {q.schedule.slots.length > 0 && (
                  <div className="mt-4 pt-4 shadow-[inset_0_1px_0_var(--color-hair)]">
                    <p className="text-sm font-semibold text-ink">{t('checkout.when')}</p>
                    <div
                      className="no-scrollbar -mx-4 mt-2 flex gap-2 overflow-x-auto px-4"
                      role="radiogroup"
                      aria-label={t('checkout.when')}
                    >
                      {q.schedule.canOrderNow && (
                        <Pill
                          active={c.scheduledFor === null}
                          onClick={() => c.setScheduledFor(null)}
                        >
                          {t('checkout.now')}
                        </Pill>
                      )}
                      {q.schedule.slots.map((slot) => (
                        <Pill
                          key={slot}
                          active={c.scheduledFor === slot}
                          onClick={() => c.setScheduledFor(slot)}
                        >
                          {slotText(slot, {
                            today: t('common.today'),
                            tomorrow: t('common.tomorrow'),
                          })}
                        </Pill>
                      ))}
                    </div>
                    {c.scheduledFor && q.schedule.prepaidOnly && (
                      <p className="mt-2 text-caption text-ink-3">{t('checkout.prepaidOnly')}</p>
                    )}
                  </div>
                )}
              </Card>

              <TipCard
                title={t('checkout.tip')}
                hint={t('checkout.tipHint')}
                noTipLabel={t('checkout.noTip')}
                presets={q.tip.presetsMinor}
                value={c.tipMinor}
                country={country}
                onChange={c.setTip}
              />

              <BillCard
                title={t('checkout.bill')}
                lines={q.bill.lines}
                total={q.bill.totalMinor}
                totalLabel={t('checkout.toPay')}
                savings={
                  q.bill.savingsMinor > 0
                    ? t('checkout.savedTotal', { amount: money(country, q.bill.savingsMinor) })
                    : null
                }
                country={country}
                fundingLabel={funding}
                whyLabel={t('checkout.why')}
                onWhy={c.showWhy}
              />

              {cashbackHint && (
                <CashbackBanner
                  title={t('cashback.hint', { percent: cashbackHint.percent })}
                  body={t('cashback.hintBody', {
                    amount: money(country, cashbackHint.amountMinor),
                  })}
                />
              )}

              {c.signedIn && (
                <PaymentSection title={t('checkout.payment')}>
                  {wallet && wallet.balanceMinor > 0 && (
                    <WalletToggleRow
                      label={t('pay.wallet')}
                      balance={money(country, wallet.balanceMinor)}
                      using={walletUsed > 0}
                      hint={
                        walletUsed > 0
                          ? t('pay.walletUsing', { amount: money(country, walletUsed) })
                          : t('pay.walletOff')
                      }
                      expiry={
                        wallet.nextExpiry
                          ? t('pay.walletExpires', {
                              amount: money(country, wallet.nextExpiry.amountMinor),
                              date: dayMonth(wallet.nextExpiry.at),
                            })
                          : null
                      }
                      on={c.useWallet}
                      onToggle={c.setUseWallet}
                    />
                  )}
                  <MethodList
                    label={t('checkout.payment')}
                    value={c.paymentMethod}
                    onChange={c.setPaymentMethod}
                    rows={q.payment.methods.map((option) => ({
                      method: option.method,
                      name: t(`pay.method.${option.method}`),
                      available: option.available,
                      caption: option.available ? option.note : option.reason,
                    }))}
                  />
                </PaymentSection>
              )}

              {q.blockers.length > 0 && (
                <Notice tone="warning" title={q.blockers[0] ?? ''}>
                  {q.blockers.length > 1 ? q.blockers.slice(1).join(' · ') : null}
                </Notice>
              )}
            </>
          )
        )}
      </div>

      <PayBar amount={q ? payable : null} caption={q ? t('checkout.toPay') : null}>
        {c.signedIn ? (
          <>
            {q && walletUsed > 0 ? (
              <PayNote
                positive
                text={t('pay.walletApplied', { amount: money(country, walletUsed) })}
              />
            ) : (
              c.payKind === 'online' && <PayNote positive={false} text={t('pay.secure')} />
            )}
            <Button full busy={c.placing} disabled={!c.canPlace} onClick={c.place}>
              {c.payKind !== 'place' && !c.placing && <Icon name="lock" size={14} />}
              {c.placing ? t('checkout.placing') : payLabel}
            </Button>
          </>
        ) : (
          <Button full onClick={() => navigate('/sign-in?next=/cart')}>
            {t('checkout.signIn')}
          </Button>
        )}
      </PayBar>

      {sheet === 'offers' && q && (
        <OffersSheet
          offers={q.offers}
          country={country}
          applied={q.appliedCoupon}
          error={q.couponError}
          labels={{
            title: t('checkout.offers'),
            enterCode: t('checkout.enterCode'),
            apply: t('checkout.apply'),
            remove: t('checkout.remove'),
            save: (amount) => t('checkout.youSave', { amount }),
            funding,
          }}
          onApply={(code) => {
            c.applyCoupon(code);
            setSheet(null);
          }}
          onRemove={() => {
            c.removeCoupon();
            setSheet(null);
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'address' && (
        <AddressSheet
          addresses={c.addresses}
          selectedId={c.address?.id ?? null}
          labels={names}
          title={t('checkout.pickAddress')}
          addLabel={t('checkout.addAddress')}
          notServed={t('address.notServed')}
          onPick={(id) => {
            c.chooseAddress(id);
            setSheet(null);
          }}
          onAdd={() => navigate('/address/new')}
          onClose={() => setSheet(null)}
        />
      )}
      {c.why && (
        <WhySheet line={c.why} closeLabel={t('checkout.gotIt')} onClose={() => c.showWhy(null)} />
      )}
    </div>
  );
}
